<!-- Patient_Drawer.vue -->
<template>
  <!-- Sidebar Mode - Collapsed Vertical Icon Strip -->
  <div data-private v-if="sidebarMode" class="h-full bg-white border-r border-gray-200 w-14 flex flex-col">
    <!-- Patient Initials at Top -->
    <div class="p-4 border-b border-gray-200">
      <button
        @click="handleSidebarTabClick('details')"
        class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center hover:bg-primary-200 transition-colors group"
        :title="patientInitials"
      >
        <span class="text-xs font-semibold text-primary-700 group-hover:text-primary-800">
          {{ patientInitials }}
        </span>
      </button>
    </div>

    <!-- Tab Icons -->
    <div class="flex-1 flex flex-col py-2">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        @click="handleSidebarTabClick(tab.id)"
        :class="[
          'relative w-full h-12 flex items-center justify-center hover:bg-gray-100 transition-colors group',
          activeTab === tab.id ? 'bg-gray-100' : ''
        ]"
        :title="tab.label"
      >
        <!-- Active Indicator Bar -->
        <div
          v-if="activeTab === tab.id"
          class="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-900"
        ></div>

        <!-- Icon -->
        <FontAwesomeIcon
          :icon="tab.icon"
          :class="[
            'text-lg transition-colors',
            activeTab === tab.id
              ? 'text-gray-900'
              : 'text-gray-500 group-hover:text-gray-700'
          ]"
        />
      </button>
    </div>

    <!-- Expand Button at Bottom -->
    <div class="p-3 border-t border-gray-200">
      <button
        @click="$emit('toggle-maximize')"
        class="w-8 h-8 rounded flex items-center justify-center hover:bg-gray-100 transition-colors group"
        title="Expand Patient Details"
      >
        <FontAwesomeIcon
          :icon="['fas', 'angles-right']"
          class="text-gray-500 group-hover:text-gray-700"
        />
      </button>
    </div>
  </div>

  <!-- Full Mode - Normal View -->
  <div data-private v-else class="h-full flex flex-col bg-gray-50">
    <!-- Tab Navigation + Patient Header -->
    <div class="flex items-center px-3 shrink-0 bg-white border-b border-gray-200">
      <!-- Icon Tabs -->
      <div class="flex gap-1">
        <template v-for="tab in visibleTabs" :key="tab.id">
          <!-- Clinical history tab with inline dropdown -->
          <div
            v-if="tab.id === 'utilization'"
            ref="clinicalDropdownContainer"
            :class="[
              'relative flex items-center border-b-2 -mb-px transition-colors',
              activeTab === 'utilization'
                ? 'border-primary-500'
                : 'border-transparent hover:border-gray-300'
            ]"
          >
            <button
              @click="activeTab = 'utilization'; clinicalDropdownOpen = !clinicalDropdownOpen"
              :class="[
                'px-3 py-2 flex items-center gap-1.5 transition-colors',
                activeTab === 'utilization'
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              ]"
              :title="tab.label"
            >
              <FontAwesomeIcon :icon="tab.icon" class="text-sm" />
              <span v-if="activeTab === 'utilization'" class="text-xs">{{ clinicalSubTabLabel }}</span>
              <FontAwesomeIcon v-if="activeTab === 'utilization'" :icon="['fas', 'chevron-down']" :class="['text-[9px] text-gray-400 transition-transform', clinicalDropdownOpen ? 'rotate-180' : '']" />
            </button>
            <div v-if="clinicalDropdownOpen" class="absolute left-0 top-full mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[160px]">
              <button
                v-for="sub in clinicalSubTabs"
                :key="sub.id"
                @click="selectClinicalSubTab(sub.id)"
                :class="[
                  'w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors',
                  clinicalSubTab === sub.id ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
                ]"
              >
                <FontAwesomeIcon :icon="sub.icon" class="w-3 text-gray-400" />
                {{ sub.label }}
              </button>
            </div>
          </div>
          <!-- Normal tabs -->
          <button
            v-else
            @click="activeTab = tab.id; clinicalDropdownOpen = false"
            :class="[
              'px-3 py-2 border-b-2 -mb-px transition-colors',
              activeTab === tab.id
                ? 'border-primary-500 text-gray-900'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            ]"
            :title="tab.label"
          >
            <FontAwesomeIcon :icon="tab.icon" class="text-sm" />
          </button>
        </template>
      </div>

      <!-- Patient Info + Close (right-aligned). min-w-0 lets the block shrink in
           the narrow split-pane header; only the NAME truncates (ellipsis) while
           DOB + MBI stay whole (shrink-0, nowrap) so the identity never squashes
           into an unreadable stack. Full name in the tooltip. -->
      <div v-if="showHeader" class="ml-auto flex items-center gap-3 min-w-0">
        <div class="sensitive flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
          <span
            class="font-medium text-gray-700 truncate min-w-0"
            :title="patientName || patientIdentifier"
          >{{ patientName || patientIdentifier }}</span>
          <template v-if="preferredName">
            <span class="text-gray-500 truncate min-w-0">"{{ preferredName }}"</span>
          </template>
          <template v-if="displayPatientDob">
            <span class="text-gray-300 shrink-0">·</span>
            <span class="shrink-0 whitespace-nowrap">DOB {{ displayPatientDob }}</span>
          </template>
          <template v-if="patientName && patientIdentifier">
            <span class="text-gray-300 shrink-0">·</span>
            <span class="text-gray-500 shrink-0 whitespace-nowrap">{{ patientIdentifier }}</span>
          </template>
        </div>
        <a
          :href="drawerPageUrl"
          target="_blank"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          title="Open in full screen"
        >
          <FontAwesomeIcon :icon="['fas', 'up-right-from-square']" class="text-sm" />
        </a>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          title="Close"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="text-sm" />
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-hidden">
      <!-- Patient Details Tab -->
      <div v-if="activeTab === 'details'" class="h-full overflow-y-auto p-4">
        <Patient_Detail_Info
          :key="patientIdentifier"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          :patient-data="patientData"
          :entity-attributes="entityAttributes"
          :entity-attributes-loading="entityDataLoading"
          @show-message="$emit('show-message', $event)"
          @preferred-name-updated="preferredName = $event"
        />
      </div>

      <!-- Utilization/Clinical History Tab -->
      <div v-if="activeTab === 'utilization'" class="h-full overflow-y-auto">
        <Patient_Clinical_History
          :key="patientIdentifier"
          ref="clinicalHistory"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          @subtab-changed="clinicalSubTab = $event"
        />
      </div>

      <!-- Engagement Tab -->
      <div v-if="activeTab === 'engagement'" class="h-full overflow-y-auto p-4">
        <Patient_Engagement
          :key="patientIdentifier"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          @show-message="$emit('show-message', $event)"
        />
      </div>

      <!-- Programs Tab -->
      <div v-if="activeTab === 'programs'" class="h-full overflow-y-auto p-4">
        <Patient_Programs
          :key="patientIdentifier"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          :initial-task-id="initialTaskId"
          :metric-category="metricCategory"
          @show-message="$emit('show-message', $event)"
          @task-updated="$emit('task-updated')"
          @open-opportunity="$emit('open-opportunity', $event)"
        />
      </div>

      <!-- Care Plan Tab -->
      <div v-if="activeTab === 'care_plan'" class="h-full overflow-y-auto">
        <Patient_CarePlan
          :key="patientIdentifier"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          @show-message="$emit('show-message', $event)"
        />
      </div>

      <!-- Care Graph Tab -->
      <div v-if="activeTab === 'care_graph'" class="h-full overflow-y-auto">
        <Patient_CareGraph
          :key="patientIdentifier"
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          @show-message="$emit('show-message', $event)"
        />
      </div>

      <!-- Care Gaps Tab -->
      <div v-if="activeTab === 'gaps'" class="h-full overflow-y-auto p-4">
        <Quality_Gap_Detail
          v-if="selectedGapData"
          :key="selectedGapData.opportunity_id"
          :gap="selectedGapData"
          :patient="patientData"
          @close="clearGap"
          @refresh="handleGapRefresh"
        />
        <div v-else>
          <Patient_Gaps
            :patient-identifier="patientIdentifier"
            :entity-id="entityId"
            :include-expired="includeExpiredGaps"
            @show-message="$emit('show-message', $event)"
            @gap-selected="openGap"
          />
        </div>
      </div>

      <!-- Care Team Tab -->
      <div v-if="activeTab === 'care_team'" class="h-full overflow-y-auto p-4">
        <Patient_CareTeam
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          :opportunity-id="firstOpportunityId"
          @show-message="$emit('show-message', $event)"
        />
      </div>

      <!-- HCC History Tab -->
      <div v-if="activeTab === 'hcc_history'" class="h-full overflow-y-auto p-4">
        <Patient_HCC_History
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
        />
      </div>

      <!-- Coding Activity Tab -->
      <div v-if="activeTab === 'coding_activity'" class="h-full overflow-y-auto">
        <Patient_Coding_Activity
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
          @open-task="$emit('open-task', $event)"
        />
      </div>

      <!-- Suspects Tab -->
      <div v-if="activeTab === 'suspects'" class="h-full overflow-y-auto p-4">
        <Patient_Suspects
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
        />
      </div>

      <div v-if="activeTab === 'insights'" class="h-full overflow-y-auto p-4">
        <Patient_Insights
          :patient-identifier="patientIdentifier"
          :entity-id="entityId"
        />
      </div>

      <!-- Messages Tab -->
      <div v-if="activeTab === 'messages'" class="h-full overflow-y-auto p-4">
        <Provider_Messages
          :patient-id="entityId"
          :patient-identifier="patientIdentifier"
          :patient-name="patientName"
        />
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getEntityWithAttributeDefinitions } from '@/services/api/entities';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import Patient_Detail_Info from './Tabs/Patient_Detail_Info.vue';
import Patient_Clinical_History from './Tabs/Patient_Clinical_History.vue';
import Patient_Engagement from './Tabs/Patient_Engagement.vue';
import Patient_Programs from './Tabs/Patient_Programs.vue';
import Patient_CarePlan from './Tabs/Patient_CarePlan.vue';
import Patient_CareGraph from './Tabs/Patient_CareGraph.vue';
import Patient_Gaps from './Tabs/Patient_Gaps.vue';
import Patient_CareTeam from './Tabs/Patient_CareTeam.vue';
import Patient_HCC_History from './Tabs/Patient_HCC_History.vue';
import Patient_Coding_Activity from './Tabs/Patient_Coding_Activity.vue';
import Patient_Suspects from './Tabs/Patient_Suspects.vue';
import Patient_Insights from './Tabs/Patient_Insights.vue';
import Provider_Messages from './Tabs/Provider_Messages.vue';
import Quality_Gap_Detail from '@/components/Quality/Quality_Gap_Detail.vue';
export default {
  name: 'Patient_Drawer',
  components: {
    FontAwesomeIcon,
    Page_Tabs,
    Patient_Detail_Info,
    Patient_Clinical_History,
    Patient_Engagement,
    Patient_Programs,
    Patient_CarePlan,
    Patient_CareGraph,
    Patient_Gaps,
    Patient_CareTeam,
    Patient_HCC_History,
    Patient_Coding_Activity,
    Patient_Suspects,
    Patient_Insights,
    Provider_Messages,

    Quality_Gap_Detail
  },

  props: {
    patientIdentifier: {
      type: String,
      required: true
    },
    entityId: {
      type: Number,
      default: null
    },
    patientData: {
      type: Object,
      default: null
    },
    enabledTabs: {
      type: Array,
      default: () => ['details', 'utilization', 'engagement']
    },
    showHeader: {
      type: Boolean,
      default: false
    },
    includeExpiredGaps: {
      type: Boolean,
      default: false
    },
    patientName: {
      type: String,
      default: ''
    },
    initialTaskId: {
      type: [Number, String],
      default: null
    },
    defaultTab: {
      type: String,
      default: null
    },
    sidebarMode: {
      type: Boolean,
      default: false
    },
    metricCategory: {
      type: String,
      default: null
    }
  },

  emits: [
    'show-message',
    'tab-changed',
    'open-task',
    'open-opportunity',

    'toggle-maximize',
    'task-updated',
    'close'
  ],

  data() {
    return {
      activeTab: 'details',
      allTabs: [
        {
          id: 'details',
          label: 'Patient Details',
          icon: ['fas', 'user']
        },
        {
          id: 'utilization',
          label: 'Clinical History',
          icon: ['fas', 'heart-pulse']
        },
        {
          id: 'engagement',
          label: 'Engagement',
          icon: ['fas', 'comment']
        },
        {
          id: 'programs',
          label: 'Programs',
          icon: ['fas', 'tasks']
        },
        {
          id: 'care_plan',
          label: 'Care Plan',
          icon: ['fas', 'clipboard-list']
        },
        {
          id: 'care_graph',
          label: 'Care Graph',
          icon: ['fas', 'diagram-project']
        },
        {
          id: 'gaps',
          label: 'Care Gaps',
          icon: ['fas', 'exclamation-triangle']
        },
        {
          id: 'care_team',
          label: 'Care Team',
          icon: ['fas', 'users']
        },
        {
          id: 'hcc_history',
          label: 'HCC History',
          icon: ['fas', 'table']
        },
        {
          id: 'coding_activity',
          label: 'Coding Activity',
          icon: ['fas', 'code-branch']
        },
        {
          id: 'suspects',
          label: 'Suspects',
          icon: ['fas', 'magnifying-glass-chart']
        },
        {
          id: 'insights',
          label: 'Risk Stratification',
          icon: ['fas', 'brain']
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: ['fas', 'envelope']
        },
      ],

      selectedGapData: null,
      clinicalDropdownOpen: false,
      clinicalSubTab: 'overview',
      clinicalSubTabs: [
        { id: 'overview', label: 'Overview', icon: ['fas', 'file-medical'] },
        { id: 'timeline', label: 'Utilization', icon: ['fas', 'heart-pulse'] },
        { id: 'adt_events', label: 'ADT Events', icon: ['fas', 'exchange-alt'] },
        { id: 'diagnosis', label: 'Diagnoses', icon: ['fas', 'file-medical'] },
        { id: 'prescriptions', label: 'Prescriptions', icon: ['fas', 'pills'] },
        { id: 'vitals', label: 'Vitals', icon: ['fas', 'heart-pulse'] },
        { id: 'labs', label: 'Labs', icon: ['fas', 'flask'] },
        { id: 'social', label: 'Social', icon: ['fas', 'seedling'] },
      ],

      entityAttributes: null,
      preferredName: null,
      entityDataLoading: false,
    };
  },

  computed: {
    ...mapGetters('tenant', ['opportunityNounPlural', 'current_tenant_slug']),
    visibleTabs() {
      // Per-tenant label overrides: IHCI renames the Engagement/Programs tabs
      // to "Activity Timeline" / "Engagement Opportunities" (Care Advisor ask
      // #9, Workstream E). Every other tenant keeps the original labels.
      const tabLabelOverrides = this.current_tenant_slug === 'ihci'
        ? { engagement: 'Activity Timeline', programs: 'Engagement Opportunities' }
        : {};
      return this.allTabs
        .filter(tab => this.enabledTabs.includes(tab.id))
        .filter(tab => tab.id !== 'messages' || canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false }))
        .map(tab => (tabLabelOverrides[tab.id]
          ? { ...tab, label: tabLabelOverrides[tab.id] }
          : tab));
    },
    clinicalSubTabLabel() {
      const sub = this.clinicalSubTabs.find(s => s.id === this.clinicalSubTab);
      return sub ? sub.label : 'Utilization';
    },
    firstOpportunityId() {
      const programs = this.patientData?.enrolled_programs;
      return programs?.length ? programs[0].id : null;
    },
    displayPatientDob() {
      const raw = this.patientData?.patient_dob || this.patientData?.bene_dob;
      if (!raw) return '';
      const parts = String(raw).substring(0, 10).split('-');
      if (parts.length !== 3) return '';
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric',
      });
    },
    drawerPageUrl() {
      let url = `/patients/${this.patientIdentifier}/drawer`;
      const params = [];
      if (this.entityId) params.push(`entity=${this.entityId}`);
      if (this.activeTab && this.activeTab !== 'details') params.push(`tab=${this.activeTab}`);
      if (params.length) url += `?${params.join('&')}`;
      return url;
    },
    patientInitials() {
      const first = this.patientData?.bene_first_name;
      const last = this.patientData?.bene_last_name;
      if (first && last) {
        return `${first[0]}${last[0]}`.toUpperCase();
      }
      return 'PT';
    }
  },

  methods: {
    _closeClinicalDropdown(e) {
      if (this.clinicalDropdownOpen) {
        const container = this.$refs.clinicalDropdownContainer;
        const el = Array.isArray(container) ? container[0] : container;
        if (el && !el.contains(e.target)) {
          this.clinicalDropdownOpen = false;
        }
      }
    },
    selectClinicalSubTab(id) {
      this.clinicalSubTab = id;
      this.clinicalDropdownOpen = false;
      this.$nextTick(() => {
        if (this.$refs.clinicalHistory) {
          this.$refs.clinicalHistory.setSubTab(id);
        }
      });
    },
    handleSidebarTabClick(tabId) {
      this.activeTab = tabId;
      this.$emit('toggle-maximize');
    },
    openGap(gap) {
      this.selectedGapData = gap;
    },
    clearGap() {
      this.selectedGapData = null;
    },
    handleGapRefresh() {
      this.$emit('task-updated');
    },
    // --- Last-used tab persistence ---------------------------------------
    // Remember the user's last selected drawer tab so the next patient opens
    // to the same tab (per-user UI pref via localStorage, matching the
    // codebase pattern, e.g. cm_split_pct). Keyed globally for the drawer, not
    // per-patient — "the last tab type I was on" is the intent.
    _tabStorageKey() {
      return 'patient_drawer_last_tab';
    },
    rememberedTab() {
      try {
        return localStorage.getItem(this._tabStorageKey()) || null;
      } catch (e) {
        return null;
      }
    },
    rememberTab(tabId) {
      if (!tabId) return;
      try {
        localStorage.setItem(this._tabStorageKey(), tabId);
      } catch (e) {
        /* localStorage unavailable — non-fatal */
      }
    },
    async fetchEntityAttributes() {
      if (!this.entityId) return;
      this.entityDataLoading = true;
      try {
        const { attributes } =
          await getEntityWithAttributeDefinitions(this.entityId);
        this.entityAttributes = attributes;
        const group = attributes['patient_info'] || [];
        const pn = group.find((a) => a.name === 'preferred_name');
        this.preferredName = pn?.value || null;
      } catch {
        this.entityAttributes = {};
        this.preferredName = null;
      } finally {
        this.entityDataLoading = false;
      }
    },
  },

  watch: {
    entityId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchEntityAttributes();
        } else {
          this.entityAttributes = null;
          this.preferredName = null;
        }
      },
    },
    activeTab(newTab) {
      this.$emit('tab-changed', newTab);
      // Remember the user's last tab so the next patient opens to the same one.
      this.rememberTab(newTab);
    },
    patientIdentifier() {
      // Open the new patient to the user's last-used tab (falls back to first
      // enabled). An explicit defaultTab prop still wins via its own watcher.
      const remembered = this.rememberedTab();
      if (this.defaultTab && this.enabledTabs.includes(this.defaultTab)) {
        this.activeTab = this.defaultTab;
      } else if (remembered && this.enabledTabs.includes(remembered)) {
        this.activeTab = remembered;
      } else if (this.enabledTabs.length > 0) {
        this.activeTab = this.enabledTabs[0];
      }
    },
    defaultTab(val) {
      if (val && this.enabledTabs.includes(val)) {
        this.activeTab = val;
      }
    },
    enabledTabs: {
      immediate: true,
      handler(newTabs) {
        // If current tab is not in enabled tabs, switch to first enabled tab
        if (!newTabs.includes(this.activeTab) && newTabs.length > 0) {
          this.activeTab = newTabs[0];
        }
      }
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this._closeClinicalDropdown);
  },
  mounted() {
    document.addEventListener('click', this._closeClinicalDropdown);
    // Initial tab precedence: explicit defaultTab prop > user's remembered tab >
    // first enabled tab. (Replaces the old hard-coded "always open to programs".)
    const remembered = this.rememberedTab();
    if (this.defaultTab && this.enabledTabs.includes(this.defaultTab)) {
      this.activeTab = this.defaultTab;
    } else if (remembered && this.enabledTabs.includes(remembered)) {
      this.activeTab = remembered;
    } else if (this.enabledTabs.length > 0) {
      this.activeTab = this.enabledTabs[0];
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar for tab navigation */
nav::-webkit-scrollbar {
  height: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 2px;
}

nav::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
